/* Ouvrir le fichier que l'utilisateur dépose, sans rien installer.
 *
 * Un .xlsx n'est qu'une archive zip contenant du XML : le navigateur sait
 * décompresser (`DecompressionStream`) et sait lire du XML (`DOMParser`). Cent
 * cinquante lignes suffisent donc là où la bibliothèque courante en pèse neuf
 * cents kilos — et traîne deux failles sans correctif. On ne lit que ce dont
 * on a besoin : les valeurs de la première feuille.
 *
 * Tout ce qui sort d'ici est un tableau de cellules brutes. L'interprétation
 * (nombres à la française, colonnes, obligatoires) appartient à `domain/import`.
 */

import type { Cellule, Rangee } from "@/domain/import";

export class FichierIllisible extends Error {}

const TAILLE_MAX = 12 * 1024 * 1024;

export async function lireClasseur(fichier: File): Promise<Rangee[]> {
  if (fichier.size > TAILLE_MAX) {
    throw new FichierIllisible(
      "Fichier trop lourd (plus de 12 Mo). Découpez-le, ou enregistrez-le en CSV.",
    );
  }
  const nom = fichier.name.toLowerCase();
  if (nom.endsWith(".xlsx")) return lireXlsx(await fichier.arrayBuffer());
  if (nom.endsWith(".csv") || nom.endsWith(".txt") || nom.endsWith(".tsv")) {
    return lireCsv(await fichier.text());
  }
  if (nom.endsWith(".xls")) {
    throw new FichierIllisible(
      "Les .xls d'avant 2007 ne sont pas lus. Dans Excel : Enregistrer sous → .xlsx ou .csv.",
    );
  }
  throw new FichierIllisible("Format non reconnu. Déposez un .xlsx ou un .csv.");
}

/* ─────────────────────────────────── CSV ────────────────────────────────── */

/** Remplace le contenu entre guillemets par des points : un « ; » cité ne
    doit pas voter pour le point-virgule quand on cherche le séparateur. */
function masque(texte: string): string {
  let dedans = false;
  let sortie = "";
  for (let i = 0; i < texte.length; i++) {
    const c = texte[i] as string;
    if (c === '"') {
      dedans = !dedans;
      sortie += '"';
    } else sortie += dedans && c !== "\n" ? "." : c;
  }
  return sortie;
}

/** Le séparateur du fichier : celui qui découpe le plus régulièrement. */
function separateur(brut: string): string {
  const texte = masque(brut);
  const debut = texte.slice(0, 4000).split(/\r?\n/).filter((l) => l.trim()).slice(0, 10);
  let meilleur = ",";
  let score = -1;
  for (const s of [";", ",", "\t", "|"]) {
    const compte = debut.map((l) => l.split(s).length);
    if (compte.length === 0) continue;
    const max = Math.max(...compte);
    if (max < 2) continue;
    // Un bon séparateur donne le même nombre de colonnes à chaque ligne.
    const regulier = compte.filter((n) => n === max).length;
    const n = max * 10 + regulier;
    if (n > score) {
      score = n;
      meilleur = s;
    }
  }
  return meilleur;
}

export function lireCsv(contenu: string): Rangee[] {
  const texte = contenu.replace(/^﻿/, "");
  const sep = separateur(texte);
  const rangees: Rangee[] = [];
  let ligne: Cellule[] = [];
  let champ = "";
  let guillemets = false;

  for (let i = 0; i < texte.length; i++) {
    const c = texte[i];
    if (guillemets) {
      if (c === '"') {
        if (texte[i + 1] === '"') {
          champ += '"';
          i++;
        } else guillemets = false;
      } else champ += c;
      continue;
    }
    if (c === '"' && champ === "") {
      guillemets = true;
    } else if (c === sep) {
      ligne.push(champ);
      champ = "";
    } else if (c === "\n") {
      ligne.push(champ);
      rangees.push(ligne);
      ligne = [];
      champ = "";
    } else if (c !== "\r") {
      champ += c;
    }
  }
  if (champ !== "" || ligne.length) {
    ligne.push(champ);
    rangees.push(ligne);
  }
  return rangees;
}

/* ────────────────────────────────── XLSX ────────────────────────────────── */

interface EntreeZip {
  nom: string;
  methode: number;
  debutDonnees: number;
  tailleCompressee: number;
}

const SIG_FIN_CENTRAL = 0x06054b50;
const SIG_CENTRAL = 0x02014b50;

/** Entrées de l'archive, lues par le répertoire central (le seul fiable). */
function entrees(vue: DataView): EntreeZip[] {
  const n = vue.byteLength;
  let fin = -1;
  // Le commentaire de fin fait au plus 65 535 octets : on ne remonte pas plus loin.
  for (let i = n - 22; i >= Math.max(0, n - 22 - 65535); i--) {
    if (vue.getUint32(i, true) === SIG_FIN_CENTRAL) {
      fin = i;
      break;
    }
  }
  if (fin < 0) throw new FichierIllisible("Ce .xlsx est illisible : archive incomplète.");

  const nombre = vue.getUint16(fin + 10, true);
  let p = vue.getUint32(fin + 16, true);
  const sorties: EntreeZip[] = [];

  for (let k = 0; k < nombre && p + 46 <= n; k++) {
    if (vue.getUint32(p, true) !== SIG_CENTRAL) break;
    const methode = vue.getUint16(p + 10, true);
    const tailleCompressee = vue.getUint32(p + 20, true);
    const lNom = vue.getUint16(p + 28, true);
    const lExtra = vue.getUint16(p + 30, true);
    const lComm = vue.getUint16(p + 32, true);
    const offsetLocal = vue.getUint32(p + 42, true);
    const nom = new TextDecoder().decode(
      new Uint8Array(vue.buffer, vue.byteOffset + p + 46, lNom),
    );
    // L'en-tête local redit les longueurs, et c'est après lui que vivent les données.
    const lNomLocal = vue.getUint16(offsetLocal + 26, true);
    const lExtraLocal = vue.getUint16(offsetLocal + 28, true);
    sorties.push({
      nom,
      methode,
      debutDonnees: offsetLocal + 30 + lNomLocal + lExtraLocal,
      tailleCompressee,
    });
    p += 46 + lNom + lExtra + lComm;
  }
  return sorties;
}

async function contenu(buf: ArrayBuffer, e: EntreeZip): Promise<string> {
  const octets = new Uint8Array(buf, e.debutDonnees, e.tailleCompressee);
  if (e.methode === 0) return new TextDecoder().decode(octets);
  if (e.methode !== 8) {
    throw new FichierIllisible("Ce .xlsx utilise une compression que le navigateur ne lit pas.");
  }
  if (typeof DecompressionStream === "undefined") {
    throw new FichierIllisible(
      "Ce navigateur ne sait pas ouvrir un .xlsx. Enregistrez le fichier en .csv.",
    );
  }
  const flux = new Blob([octets]).stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Response(flux).text();
}

/** « AB12 » → colonne 27. */
function colonneDe(ref: string): number {
  let n = 0;
  for (const c of ref) {
    const code = c.charCodeAt(0);
    if (code < 65 || code > 90) break;
    n = n * 26 + (code - 64);
  }
  return n - 1;
}

function textes(noeud: Document | Element, balise: string): string {
  return [...noeud.getElementsByTagName(balise)].map((n) => n.textContent ?? "").join("");
}

async function lireXlsx(buf: ArrayBuffer): Promise<Rangee[]> {
  const vue = new DataView(buf);
  const liste = entrees(vue);

  const feuille =
    liste.find((e) => e.nom === "xl/worksheets/sheet1.xml") ??
    liste.filter((e) => /^xl\/worksheets\/[^/]+\.xml$/.test(e.nom))
      .sort((a, b) => a.nom.localeCompare(b.nom))[0];
  if (!feuille) throw new FichierIllisible("Ce .xlsx ne contient aucune feuille lisible.");

  const partagees: string[] = [];
  const sst = liste.find((e) => e.nom === "xl/sharedStrings.xml");
  if (sst) {
    const doc = new DOMParser().parseFromString(await contenu(buf, sst), "application/xml");
    for (const si of doc.getElementsByTagName("si")) {
      /* Une chaîne peut être découpée en morceaux (mise en forme partielle) :
         on recolle, en écartant les annotations phonétiques. */
      const rubis = new Set([...si.getElementsByTagName("rPh")].flatMap((r) => [
        ...r.getElementsByTagName("t"),
      ]));
      partagees.push(
        [...si.getElementsByTagName("t")]
          .filter((t) => !rubis.has(t))
          .map((t) => t.textContent ?? "")
          .join(""),
      );
    }
  }

  const doc = new DOMParser().parseFromString(await contenu(buf, feuille), "application/xml");
  if (doc.getElementsByTagName("parsererror").length) {
    throw new FichierIllisible("Ce .xlsx est illisible : feuille mal formée.");
  }

  const rangees: Rangee[] = [];
  for (const row of doc.getElementsByTagName("row")) {
    const cellules: Cellule[] = [];
    for (const c of row.getElementsByTagName("c")) {
      const i = colonneDe(c.getAttribute("r") ?? "");
      if (i < 0) continue;
      const type = c.getAttribute("t");
      let v: Cellule = null;
      if (type === "s") {
        const k = Number(textes(c, "v"));
        v = partagees[k] ?? null;
      } else if (type === "inlineStr") {
        v = textes(c, "t");
      } else if (type === "b") {
        v = textes(c, "v") === "1";
      } else {
        const brut = textes(c, "v");
        if (brut === "") v = null;
        else {
          const n = Number(brut);
          v = Number.isFinite(n) ? n : brut;
        }
      }
      while (cellules.length < i) cellules.push(null);
      cellules[i] = v;
    }
    const r = Number(row.getAttribute("r") ?? "0") - 1;
    // Une feuille peut sauter des lignes vides : on les rétablit pour que les
    // numéros rendus soient bien ceux du tableur.
    while (r > 0 && rangees.length < r) rangees.push([]);
    rangees.push(cellules);
  }
  return rangees;
}

/* ─────────────────────────── le fichier d'exemple ───────────────────────── */

/** Un CSV téléchargeable : en-têtes du modèle, et une ligne d'aide. */
export function modeleCsv(titres: readonly string[], aides: readonly string[]): string {
  // On cite tout ce qui contient un séparateur possible, pas seulement le nôtre :
  // c'est ce qui permet au fichier de se relire sans ambiguïté.
  const echappe = (v: string) => (/[;,\t|"\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  return "﻿" + [titres, aides].map((l) => l.map(echappe).join(";")).join("\r\n") + "\r\n";
}
